import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { floatingSheet, theme, inputStyle } from "../theme";
import { api, getViewingOrgId } from "../api/client";
import { useFrameworks, FRAMEWORK_LABELS, withRequirementFrameworks } from "../context/FrameworksContext";
import type { FrameworkId } from "../context/FrameworksContext";
import { displayControlRef } from "../lib/controlRef";

// ── Picking a control, clause or criterion ───────────────────────────────────
//
// Wherever the platform asks "which control does this serve?", it now asks with
// a picker over the organisation's real controls rather than a text box. A typed
// string looked reasonable and was almost always wrong: "A.5.24" is a genuine
// ISO 27001 id, but every other standard's ids are prefixed ("iso9001-4.1",
// "iso42001-B.1", "ncsc-caf-a1.a", "legal-dora-19"), so a typed value rarely
// matched anything — and nothing matching a `Control.id` means nothing lines up
// with the SOA, the tag stores, the evidence pack or the AI context, all of
// which are keyed by that id.
//
// The list comes from GET /controls/soa, which is already the organisation's own
// view of the catalogue: role framework limits and the client department
// visibility rule are applied there (routes/controls.routes.ts), so this does
// not re-implement either and cannot drift from the SOA. It is narrowed here to
// the standards the organisation is actually enrolled in, which is a
// convenience, not a boundary — the server validates the id on the way in.

export interface ControlOption {
  id: string;
  name: string;
  frameworkId: string;
  /** For a legal obligation, the law it comes from ("NIS2 Directive"). */
  domain?: string | null;
}

interface SoaResponse {
  soa: Array<{ control: { id: string; name: string; frameworkId: string; sortOrder: number; domain?: string | null } }>;
}

let cache: ControlOption[] | null = null;
let cacheOrg: string | null = null;
let inflight: Promise<ControlOption[]> | null = null;
let inflightOrg: string | null = null;

/** Loaded once per page, on first use — the catalogue is large and rarely needed. */
function loadControls(): Promise<ControlOption[]> {
  // The catalogue is the organisation being looked at: moving to another client in the same tab starts again.
  const org = getViewingOrgId() ?? "own";
  if (cache && cacheOrg === org) return Promise.resolve(cache);
  if (inflight && inflightOrg === org) return inflight;
  inflightOrg = org;
  inflight = api.get<SoaResponse>("/controls/soa")
    .then((d) => {
      const loaded = (d.soa ?? []).map((r) => ({ id: r.control.id, name: r.control.name, frameworkId: r.control.frameworkId, domain: r.control.domain ?? null }));
      if (inflightOrg === org) { cache = loaded; cacheOrg = org; }
      return loaded;
    })
    .catch(() => [])
    .finally(() => { inflight = null; });
  return inflight;
}

/** Forgets the cached catalogue — call when the viewed organisation changes. */
export function resetControlCache() { cache = null; cacheOrg = null; }

export function useControlOptions(): { options: ControlOption[]; loading: boolean } {
  const org = getViewingOrgId() ?? "own";
  const [options, setOptions] = useState<ControlOption[]>(cache && cacheOrg === org ? cache : []);
  const [loading, setLoading] = useState(!(cache && cacheOrg === org));
  const { enrolled } = useFrameworks();

  useEffect(() => {
    let live = true;
    loadControls().then((all) => {
      if (!live) return;
      setOptions(all);
      setLoading(false);
    });
    return () => { live = false; };
  }, [org]);

  // Narrowed to what the organisation is enrolled in, plus the rows an enrolment is assessed
  // against (Cyber Essentials Plus → the Cyber Essentials requirements). `legal` is not a
  // `FrameworkId` an organisation enrols in — it is the data protection
  // obligations, which are always in scope where the laws are — so it is kept.
  const inScope = useMemo(() => new Set<string>([...withRequirementFrameworks(enrolled), "legal"]), [enrolled]);
  const scoped = useMemo(() => options.filter((c) => inScope.has(c.frameworkId)), [options, inScope]);

  return { options: scoped, loading };
}

const labelFor = (frameworkId: string) =>
  FRAMEWORK_LABELS[frameworkId as FrameworkId] ?? (frameworkId === "legal" ? "Legal obligations" : frameworkId);

export function controlDisplay(option: ControlOption): string {
  return `${option.id} — ${option.name}`;
}

/**
 * A searchable control picker. Reports the control **and** the standard it
 * belongs to, because the two must agree: the server takes the framework from
 * the control rather than from the caller, and a field that only sent an id
 * left `frameworkId` empty on every row it wrote.
 *
 * `value` may name a control the picker cannot offer — a row written before this
 * existed, or one from a standard since dropped. That is shown as-is with a note
 * rather than silently cleared, because it is what the record says.
 */
export function ControlPicker({ value, frameworkId, onlyFrameworks, onChange, placeholder = "Search controls and clauses…", disabled }: {
  value: string | null;
  frameworkId?: string | null;
  /** Offer only the controls of these standards (an audit offers the clauses of the standards it covers). */
  onlyFrameworks?: readonly string[];
  onChange: (next: { controlId: string | null; frameworkId: string | null }) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const { options, loading } = useControlOptions();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const selected = value ? options.find((c) => c.id === value) ?? null : null;

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const offered = onlyFrameworks ? options.filter((c) => onlyFrameworks.includes(c.frameworkId)) : options;
    const matched = q
      ? offered.filter((c) => c.id.toLowerCase().includes(q) || displayControlRef(c.id).toLowerCase().includes(q) || c.name.toLowerCase().includes(q))
      : offered;
    const byFramework = new Map<string, ControlOption[]>();
    for (const c of matched.slice(0, 400)) {
      const list = byFramework.get(c.frameworkId);
      if (list) list.push(c); else byFramework.set(c.frameworkId, [c]);
    }
    return [...byFramework.entries()].sort((a, b) => labelFor(a[0]).localeCompare(labelFor(b[0])));
  }, [options, query, onlyFrameworks]);

  const total = groups.reduce((n, [, list]) => n + list.length, 0);

  if (value && !open) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 3,
          background: theme.color.accentBg, color: theme.color.accentText, border: `1px solid ${theme.color.accent}33`,
          fontSize: 12, fontWeight: 600, maxWidth: "100%",
        }}>
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{displayControlRef(value)}</span>
          {selected && <span style={{ fontWeight: 400 }}>{selected.name}</span>}
          {selected && <span style={{ fontSize: 10, color: theme.color.muted }}>{labelFor(selected.frameworkId)}</span>}
        </span>
        {!selected && !loading && (
          <span style={{ fontSize: 11, color: theme.color.warningText }} title="Recorded before this was a picker, or from a standard the organisation no longer holds. Pick a control to replace it.">
            not in the catalogue
          </span>
        )}
        {!disabled && (
          <>
            <button type="button" onClick={() => { setQuery(""); setOpen(true); }}
              style={{ background: "none", border: `1px solid ${theme.color.border}`, borderRadius: 3, color: theme.color.muted, fontSize: 11, padding: "3px 9px", cursor: "pointer" }}>
              Change
            </button>
            <button type="button" onClick={() => onChange({ controlId: null, frameworkId: null })} title="Clear"
              style={{ background: "none", border: "none", color: theme.color.faint, cursor: "pointer", padding: 2, display: "inline-flex" }}>
              <X size={13} />
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      <div style={{ position: "relative" }}>
        <Search size={13} style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: theme.color.faint, pointerEvents: "none" }} />
        <input
          value={query}
          disabled={disabled}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={loading ? "Loading controls…" : placeholder}
          style={{ ...inputStyle, paddingLeft: 28 }}
        />
      </div>
      {open && !disabled && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 60,
          ...floatingSheet, maxHeight: 280, overflowY: "auto", padding: "6px 0",
        }}>
          {loading && <div style={{ padding: "8px 12px", fontSize: 12, color: theme.color.faint }}>Loading…</div>}
          {!loading && total === 0 && (
            <div style={{ padding: "8px 12px", fontSize: 12, color: theme.color.faint }}>
              {options.length === 0
                ? "No controls — the organisation is not enrolled in a standard with a control catalogue yet."
                : "Nothing matches that."}
            </div>
          )}
          {groups.map(([fw, list]) => (
            <div key={fw}>
              <div style={{ fontSize: 10, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.05em", padding: "6px 12px 3px" }}>
                {labelFor(fw)}
              </div>
              {list.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => { onChange({ controlId: c.id, frameworkId: c.frameworkId }); setOpen(false); setQuery(""); }}
                  style={{
                    display: "block", width: "100%", textAlign: "left", background: "transparent", border: "none",
                    padding: "5px 12px", cursor: "pointer", fontSize: 12, color: theme.color.text,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = theme.color.panelAlt)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <span style={{ fontVariantNumeric: "tabular-nums", color: theme.color.accentText, marginRight: 8 }}>{displayControlRef(c.id)}</span>
                  {c.name}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
      {frameworkId && !value && (
        <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 4 }}>{labelFor(frameworkId)}</div>
      )}
    </div>
  );
}
